import * as XLSX from "xlsx";

/**
 * Lightweight client-side parse used ONLY for the pre-upload preview
 * (row count + duplicate-title detection), mirroring how the Service Features
 * bulk upload previews before sending the file to the backend. The backend
 * remains the source of truth for full parsing, validation, and writes.
 */

/** Read the "Feature Title" cell case-insensitively. */
const getTitle = (row: Record<string, unknown>): string => {
  const key = Object.keys(row).find(
    (k) => k.trim().toLowerCase() === "feature title"
  );
  if (!key) return "";
  const value = row[key];
  return value === undefined || value === null ? "" : String(value).trim();
};

export interface WhatsNewFilePreview {
  totalRows: number;
  titles: string[];
}

/**
 * Parse an .xlsx / .xls / .csv file and return the non-empty row count and the
 * list of Feature Titles found (for duplicate detection in the preview).
 */
export const parseWhatsNewFileForPreview = (
  file: File
): Promise<WhatsNewFilePreview> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const firstSheet = workbook.SheetNames[0];
        const rows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheet], {
          defval: "",
        }) as Array<Record<string, unknown>>;

        // A row counts as data if any cell is non-empty.
        const nonEmpty = rows.filter((row) =>
          Object.values(row).some((v) => v !== undefined && String(v).trim() !== "")
        );

        resolve({
          totalRows: nonEmpty.length,
          titles: nonEmpty.map(getTitle).filter((t) => t !== ""),
        });
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = reject;
    reader.readAsBinaryString(file);
  });
};
